import { useEffect, useMemo, useState } from 'react'
import { Empty, Segmented, Spin, Tree } from 'antd'
import { FolderOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import type { KnowledgeScope } from '../../constants/knowledgeScope'
import { buildFolderTreeData, type FolderLabel } from '../knowledgeFolderUtils'
import type { FolderDimensionOption } from './DimensionMultiBrowsePanel'

type Props = {
  scope: KnowledgeScope
  dimensions: FolderDimensionOption[]
  dimensionIds?: number[]
  value?: number[]
  onChange?: (folderIds: number[]) => void
}

const EMPTY_IDS: number[] = []

function dimensionLabel(dim: FolderDimensionOption): string {
  return dim.is_manual ? '系统目录' : dim.name
}

export function FolderMultiBrowsePanel({
  scope,
  dimensions,
  dimensionIds,
  value,
  onChange,
}: Props) {
  const selectedIds = value ?? EMPTY_IDS
  const filterDimIds = dimensionIds ?? EMPTY_IDS
  const availableDimensions = useMemo(() => {
    if (!filterDimIds.length) return dimensions
    const allowed = new Set(filterDimIds)
    return dimensions.filter((d) => allowed.has(d.id))
  }, [dimensions, filterDimIds])

  const [activeDimensionId, setActiveDimensionId] = useState<number | undefined>(
    () => availableDimensions[0]?.id,
  )
  const [loading, setLoading] = useState(false)
  const [folders, setFolders] = useState<FolderLabel[]>([])

  useEffect(() => {
    if (!availableDimensions.length) {
      setActiveDimensionId(undefined)
      return
    }
    if (activeDimensionId == null || !availableDimensions.some((d) => d.id === activeDimensionId)) {
      setActiveDimensionId(availableDimensions[0].id)
    }
  }, [availableDimensions, activeDimensionId])

  useEffect(() => {
    if (activeDimensionId == null) {
      setFolders([])
      return
    }
    let cancelled = false
    setLoading(true)
    adminApi
      .get<{ items: FolderLabel[] }>('/admin/knowledge/folders/labels', {
        params: { scope, dimension_id: activeDimensionId },
      })
      .then((r) => {
        if (!cancelled) setFolders(r.data.items || [])
      })
      .catch(() => {
        if (!cancelled) setFolders([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [activeDimensionId, scope])

  const treeData = useMemo(
    () =>
      buildFolderTreeData(folders).map((node) => ({
        ...node,
        icon: <FolderOutlined />,
      })),
    [folders],
  )

  const folderIdSet = useMemo(() => new Set(folders.map((f) => f.id)), [folders])
  const checkedKeys = selectedIds.filter((id) => folderIdSet.has(id))

  if (!availableDimensions.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="请先选择虚拟分类" />
  }

  return (
    <div className="app-browse-folder-multi">
      {availableDimensions.length > 1 ? (
        <Segmented
          size="small"
          block
          className="app-browse-folder-multi-tabs"
          value={activeDimensionId}
          options={availableDimensions.map((d) => ({
            value: d.id,
            label: dimensionLabel(d),
          }))}
          onChange={(v) => setActiveDimensionId(v as number)}
        />
      ) : null}
      {loading ? (
        <div className="app-browse-folder-multi-loading">
          <Spin size="small" />
        </div>
      ) : !treeData.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无文件夹" />
      ) : (
        <Tree
          checkable
          showIcon
          blockNode
          defaultExpandAll
          treeData={treeData}
          checkedKeys={checkedKeys}
          onCheck={(keys) => {
            const raw = Array.isArray(keys) ? keys : keys.checked
            const picked = raw
              .map((k) => (typeof k === 'number' ? k : Number(k)))
              .filter((id) => Number.isFinite(id) && id > 0)
            const others = selectedIds.filter((id) => !folderIdSet.has(id))
            onChange?.([...others, ...picked].sort((a, b) => a - b))
          }}
        />
      )}
    </div>
  )
}
